import React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';

import ConfirmationModal from '../common/ConfirmationModal';
import {
  addFieldToGroup,
  addLinkedGroup,
  getConditions,
  listFormFields,
  pruneConditions,
  removeFieldFromGroup,
  removeLinkedGroup,
  renameLinkedGroup,
  setFieldRequired,
  withConditions,
} from './utils/formConditions';


const FILLED_BY_LABELS = { lab: 'лаборатория', expert: 'эксперт' };

// Модал один на вкладку: если держать его в карточке, он исчезнет вместе с удалённой группой
// раньше, чем Bootstrap закроет окно и уберёт затемнение.
const DELETE_GROUP_MODAL_ID = 'confirmLinkedGroupDeleteModal';

function fieldCaption(field) {
  return field.title ? `${field.name} — ${field.title}` : field.name;
}

// Стили и поиск — как у выбора формы на странице загрузки заданий из 1С (TasksLoad).
const selectStyles = {
  container: (base) => ({ ...base, width: '100%', maxWidth: '520px' }),
  control: (base) => ({
    ...base,
    minHeight: '38px',
    borderColor: '#ced4da',
    boxShadow: 'none',
    '&:hover': { borderColor: '#86b7fe' },
  }),
};

const filterFieldOption = (option, inputValue) => {
  const search = inputValue.trim().toLowerCase();
  if (!search) return true;
  return [option.data.name, option.data.title, option.data.layoutGroup, option.label]
    .some((text) => text.toLowerCase().includes(search));
};


function LinkedGroupCard({ group, fields, fieldsByName, onRename, onRemove, onAddField, onRemoveField }) {
  // Строка поиска управляемая: react-select очищает её после выбора, а здесь фильтр нужен
  // для добавления нескольких полей подряд. Сбрасывается, только когда список теряет фокус.
  const [fieldSearch, setFieldSearch] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const nameEditorRef = useRef(null);

  // Выход по клику снаружи ловим на mousedown документа, а не через blur поля:
  // blur убрал бы галочку до клика, и клик пришёлся бы на появившийся карандаш.
  useEffect(() => {
    if (!isEditingName) return undefined;
    const handleMouseDown = (event) => {
      if (nameEditorRef.current && !nameEditorRef.current.contains(event.target)) {
        setIsEditingName(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isEditingName]);

  const availableOptions = useMemo(() => {
    const byLayoutGroup = new Map();
    for (const field of fields) {
      if (group.fields.includes(field.name)) continue;
      const layoutGroup = field.layoutGroup === null ? 'Поля вне макета' : field.layoutGroup || 'Без названия';
      if (!byLayoutGroup.has(layoutGroup)) byLayoutGroup.set(layoutGroup, []);
      byLayoutGroup.get(layoutGroup).push({
        value: field.name,
        label: fieldCaption(field),
        name: field.name,
        title: field.title,
        layoutGroup,
      });
    }
    return [...byLayoutGroup.entries()].map(([label, options]) => ({ label, options }));
  }, [fields, group.fields]);

  return (
    <div className="card mb-3 mt-4">
      <div className="card-body">
        <div className="d-flex align-items-center mb-3">
          <div ref={nameEditorRef} className="d-flex align-items-center flex-grow-1" style={{ maxWidth: '480px' }}>
            {isEditingName ? (
              <>
                <button
                  type="button"
                  className="btn editor-button me-2"
                  title="Завершить редактирование"
                  onClick={() => setIsEditingName(false)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-check-square" viewBox="0 0 16 16">
                    <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z"/>
                    <path d="M10.97 4.97a.75.75 0 0 1 1.071 1.05l-3.992 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z"/>
                  </svg>
                </button>
                <input
                  className="form-control"
                  aria-label="Название группы"
                  value={group.name}
                  onChange={(e) => onRename(e.target.value)}
                  autoFocus
                />
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn editor-button me-2"
                  title="Изменить название группы"
                  onClick={() => setIsEditingName(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-pencil-square" viewBox="0 0 16 16">
                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/>
                  </svg>
                </button>
                <h6 className="mb-0">
                  {group.name || <span className="text-muted fw-normal">Без названия</span>}
                </h6>
              </>
            )}
          </div>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm ms-auto"
            data-bs-toggle="modal"
            data-bs-target={'#' + DELETE_GROUP_MODAL_ID}
            onClick={onRemove}
          >
            Удалить группу
          </button>
        </div>

        <div className="mb-3">
          {group.fields.length === 0 ? (
            <span className="text-muted">В группе пока нет полей</span>
          ) : (
            group.fields.map((name) => (
              <span key={name} className="lk-bordered-label d-inline-flex align-items-center me-2 mb-2 px-2 py-1">
                <span>{fieldsByName.get(name) ? fieldCaption(fieldsByName.get(name)) : name}</span>
                {fieldsByName.get(name)?.layoutGroup ? (
                  <span className="text-muted small ms-2">{fieldsByName.get(name).layoutGroup}</span>
                ) : null}
                <button
                  type="button"
                  className="btn-close ms-2"
                  style={{ fontSize: '0.6rem' }}
                  aria-label={`Исключить поле ${name} из группы`}
                  onClick={() => onRemoveField(name)}
                />
              </span>
            ))
          )}
        </div>

        <div className="d-flex align-items-center">
          <Select
            className="lk-react-select"
            classNamePrefix="lk-react-select"
            options={availableOptions}
            value={null}
            onChange={(option) => option && onAddField(option.value)}
            inputValue={fieldSearch}
            onInputChange={(value, { action }) => {
              if (action === 'input-change') setFieldSearch(value);
              if (action === 'input-blur') setFieldSearch('');
            }}
            closeMenuOnSelect={false}
            filterOption={filterFieldOption}
            isSearchable
            isDisabled={availableOptions.length === 0}
            placeholder="Добавить поле в группу…"
            noOptionsMessage={() => "Поля не найдены"}
            styles={selectStyles}
          />
          {group.fields.length < 2 ? (
            <span className="text-warning-emphasis small ms-3">
              Условие проверяется, когда в группе не меньше двух полей
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}


function ConditionsTab({ schema, uiSchema, setSchema, setIsDirty }) {
  const [filter, setFilter] = useState('');
  const [groupToRemove, setGroupToRemove] = useState(null);

  const fields = useMemo(() => listFormFields(schema, uiSchema), [schema, uiSchema]);
  const fieldsByName = useMemo(() => new Map(fields.map((field) => [field.name, field])), [fields]);
  const conditions = useMemo(
    () => pruneConditions(getConditions(schema), schema?.properties),
    [schema]
  );

  const groupNamesByField = useMemo(() => {
    const result = new Map();
    for (const group of conditions.linked_groups) {
      for (const name of group.fields) {
        result.set(name, [...(result.get(name) ?? []), group.name]);
      }
    }
    return result;
  }, [conditions]);

  const visibleFields = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return fields;
    return fields.filter((field) =>
      [field.name, field.title, field.layoutGroup ?? ''].some((text) => text.toLowerCase().includes(query))
    );
  }, [fields, filter]);

  function updateConditions(newConditions) {
    setIsDirty(true);
    setSchema(withConditions(schema, newConditions));
  }

  function removeConfirmedGroup() {
    if (groupToRemove) {
      updateConditions(removeLinkedGroup(conditions, groupToRemove.id));
    }
    setGroupToRemove(null);
  }

  return (
    <div className="mt-3 mb-5">
      <ConfirmationModal
        modalId={DELETE_GROUP_MODAL_ID}
        headerText="Связанная группа полей"
        messageText={[
          `Удалить группу «${groupToRemove?.name || 'без названия'}»?`,
          'Поля формы останутся, удалится только условие их совместного заполнения.',
        ]}
        confirmButtonText="Удалить"
        Action={removeConfirmedGroup}
        danger
      />

      <h5 className='mt-4'>Связанные группы полей</h5>
      <p className="text-muted small mb-3">
        Если в группе заполнено хотя бы одно поле, при отправке задания на проверку остальные поля группы тоже
        должны быть заполнены. В группу можно добавить поля из разных групп макетов формы.
      </p>

      {conditions.linked_groups.map((group) => (
        <LinkedGroupCard
          key={group.id}
          group={group}
          fields={fields}
          fieldsByName={fieldsByName}
          onRename={(name) => updateConditions(renameLinkedGroup(conditions, group.id, name))}
          onRemove={() => setGroupToRemove(group)}
          onAddField={(name) => updateConditions(addFieldToGroup(conditions, group.id, name))}
          onRemoveField={(name) => updateConditions(removeFieldFromGroup(conditions, group.id, name))}
        />
      ))}

      <button
        type="button"
        className="btn lk-button mb-5"
        onClick={() => updateConditions(addLinkedGroup(conditions))}
        disabled={fields.length < 2}
      >
        Добавить группу
      </button>

      <h5 className='mt-4'>Обязательные поля</h5>
      <p className="text-muted small mb-3">
        Отмеченные поля должны быть заполнены перед отправкой задания на проверку. Поле проверяется у того, кто
        его заполняет: у лаборатории или у эксперта.
      </p>

      <input
        className="form-control mb-3"
        style={{ maxWidth: '420px' }}
        placeholder="Поиск по имени, названию или группе макетов"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      {fields.length === 0 ? (
        <p className="text-muted">В форме пока нет полей</p>
      ) : (
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th style={{ width: '130px' }}>Обязательное</th>
              <th>Поле</th>
              <th>Группа макетов</th>
              <th>Кем заполняется</th>
              <th>Связанные группы</th>
            </tr>
          </thead>
          <tbody>
            {visibleFields.map((field) => (
              <tr key={field.name}>
                <td className="text-center">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    aria-label={`Обязательное поле ${field.name}`}
                    checked={conditions.required_fields.includes(field.name)}
                    onChange={(e) =>
                      updateConditions(setFieldRequired(conditions, field.name, e.target.checked))
                    }
                  />
                </td>
                <td>{fieldCaption(field)}</td>
                <td>{field.layoutGroup ?? <span className="text-muted">вне макета</span>}</td>
                <td>{FILLED_BY_LABELS[field.filled] ?? field.filled}</td>
                <td>{(groupNamesByField.get(field.name) ?? []).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default ConditionsTab;
